import { useCallback, useEffect, useMemo, useState } from "react";
import { History, Loader2, X, UserCircle2 } from "lucide-react";
import { API, formatApiErrorDetail } from "../auth/api";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
];

function dayKey(iso) {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function clockTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function ActivityDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [actor, setActor] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = actor ? `&actor=${encodeURIComponent(actor)}` : "";
      const { data: d } = await API.get(`/activity?days=${days}${qs}`);
      setData(d);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [days, actor]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const item of data.items) {
      const k = dayKey(item.ts);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(item);
    }
    return Array.from(map.entries());
  }, [data]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/70 backdrop-blur-sm p-6 overflow-auto"
      data-testid="activity-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Team activity"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl border border-white/10 bg-[#0a0a0c] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <History className="w-3.5 h-3.5 text-cyan-300" />
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc-200">
              Team Activity
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="activity-dialog-close"
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              data-testid={`activity-range-${r.days}`}
              className={`px-2.5 py-1 border font-mono text-[9px] tracking-[0.18em] uppercase transition-colors ${
                days === r.days
                  ? "border-cyan-400 bg-cyan-400/10 text-cyan-200"
                  : "border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              {r.label}
            </button>
          ))}
          <select
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            data-testid="activity-actor-filter"
            className="ml-auto bg-[#09090B] border border-white/10 focus:border-cyan-400/60 outline-none px-2 py-1 font-mono text-[11px] text-zinc-100 max-w-[14rem]"
          >
            <option value="">Everyone</option>
            {(data?.actors || []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

        {data && data.per_actor.length > 0 && (
          <div
            className="px-5 py-3 border-b border-white/10 flex flex-wrap gap-x-5 gap-y-1"
            data-testid="activity-leaderboard"
          >
            {data.per_actor.slice(0, 6).map((p) => (
              <span key={p.actor} className="font-mono text-[10px] text-zinc-400">
                <span className="text-zinc-200">{p.actor}</span> {p.events}
              </span>
            ))}
            <span className="font-mono text-[10px] text-zinc-600 ml-auto">
              {data.total} actions · {data.days}d
            </span>
          </div>
        )}

        {error && (
          <div
            className="mx-5 mt-4 border border-rose-400/30 bg-rose-400/5 px-3 py-2 font-mono text-[11px] text-rose-200"
            data-testid="activity-error"
          >
            {error}
          </div>
        )}

        <div className="max-h-[60vh] overflow-auto" data-testid="activity-feed">
          {loading ? (
            <div className="px-5 py-6 flex items-center gap-2 font-mono text-[11px] text-zinc-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading activity…
            </div>
          ) : grouped.length === 0 ? (
            <div
              className="px-5 py-8 font-mono text-[11px] text-zinc-500 text-center"
              data-testid="activity-empty"
            >
              No activity in this period.
            </div>
          ) : (
            grouped.map(([day, items]) => (
              <div key={day}>
                <div className="px-5 py-2 bg-[#0d0d10] border-y border-white/5 font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500">
                  {day} · {items.length}
                </div>
                <div className="divide-y divide-white/5">
                  {items.map((it) => (
                    <div
                      key={it.id}
                      className="px-5 py-2.5 flex items-start gap-3"
                      data-testid={`activity-item-${it.id}`}
                    >
                      <UserCircle2 className="w-3.5 h-3.5 text-zinc-600 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-zinc-200">
                          <span className="text-zinc-100">{it.actor}</span>{" "}
                          <span className="text-zinc-400">{it.label}</span>
                        </div>
                        {it.fields.length > 0 && (
                          <div className="font-mono text-[10px] text-zinc-500 mt-0.5 break-words">
                            fields: {it.fields.join(", ")}
                          </div>
                        )}
                      </div>
                      <span className="font-mono text-[10px] text-zinc-600 shrink-0">
                        {clockTime(it.ts)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-5 py-2 border-t border-white/10 font-mono text-[9px] tracking-[0.18em] uppercase text-zinc-600">
          Field names only · contact values are never recorded
        </div>
      </div>
    </div>
  );
}
